import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Observable, forkJoin, from, of } from 'rxjs';

import {
    LiquidacionesService,
    LiquidacionAgencia as LiquidacionAgenciaService,
    ExtraRows,
} from '../../../services/liquidaciones.service';
import { ParameterService } from '../../../services/parameter.service';
import { LoggerService } from '../../../core/services/logger.service';
import { EXTRA_SERVICIOS_FALLBACK_MESSAGE } from '../../../services/liquidaciones-extra-servicios-fallback.util';
import {
    construirFiltrosLiquidaciones,
    filtrarPorSeleccion,
    EstadoFiltrosLiquidaciones,
    TAMANO_PAGINA_EXPORT,
    MAX_PAGINAS_EXPORT,
} from './liquidaciones-agencia-filtros.util';
import { agregarHojasLiquidacionAlWorkbook as agregarHojasLiquidacionAlWorkbookUtil } from './liquidaciones-agencia-excel-workbook.util';
import {
    ConsolidadoRow,
    mergeConsolidadoByContainer,
} from './liquidaciones-agencia-consolidado.util';
import type {
    LiquidacionAgenciaLocal,
    LiquidacionAgenciaTotales,
} from './liquidaciones-agencia.component';

const LOG_CONTEXT = 'LiquidacionesAgenciaComponent';

/** Colores de marca OPB usados en las tres hojas de Excel que arma este servicio. */
const OPB_COLORS = {
    primary: '1e3a8a', // Azul primario
    accent: 'ef4444', // Rojo acento
    secondary: '3b82f6', // Azul secundario
    dark: '1e293b', // Azul oscuro
    gray: '64748b', // Gris
    light: 'f1f5f9', // Gris claro
    white: 'ffffff', // Blanco
};

/**
 * Un aviso que el servicio necesita mostrarle al usuario. El SERVICIO decide el
 * texto y la severidad (duración/panelClass); el COMPONENTE es quien sabe mostrar
 * un snackbar de Angular Material — por eso el servicio nunca importa
 * `MatSnackBar` y en su lugar invoca este callback exactamente en el mismo punto
 * (y en el mismo orden) donde antes llamaba a `this.snackBar.open(...)`.
 */
export interface SnackbarMensaje {
    texto: string;
    duracionMs: number;
    panelClass: string[];
}

export type AvisarSnackbar = (mensaje: SnackbarMensaje) => void;

/**
 * Orquestación de las tres exportaciones Excel de Liquidaciones Agencia
 * (individual, fusionado y listado completo), extraída de
 * `LiquidacionesAgenciaComponent` en dos PRs: PR-7c trajo `generarExcelIndividual`
 * y `generarExcelFusionado`; PR-7d agrega acá `exportarExcelListado`.
 *
 * Diseño:
 * - Recibe explícitamente lo que necesita (datos, ids, callbacks de
 *   mapeo/formato) en vez de leer `this` de un componente.
 * - Nunca conoce `MatSnackBar` ni las flags de UI (`isGeneratingExcel`, etc.):
 *   le avisa al llamador vía el callback `avisar`, y el llamador (el componente)
 *   decide qué hacer con eso (mostrar el snackbar, togglear una flag).
 * - El armado del workbook en sí (hojas, estilos, headers) sigue viviendo en los
 *   utils puros de PR-7b (`liquidaciones-agencia-excel-workbook.util.ts` /
 *   `liquidaciones-agencia-excel-estilos.util.ts`); este servicio orquesta el
 *   fetch de datos, decide qué hojas arma, y maneja la descarga (I/O de DOM).
 */
@Injectable({
    providedIn: 'root',
})
export class LiquidacionesAgenciaExportService {
    constructor(
        // eslint-disable-next-line no-unused-vars
        private liquidacionesService: LiquidacionesService,
        // eslint-disable-next-line no-unused-vars
        private parameterService: ParameterService,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService
    ) {}

    /**
     * Carga el logo OPB para estampar en el header de cada hoja. Intenta primero
     * con el `base href` de Angular y, si no responde OK, reintenta sin él.
     * Nunca lanza: si falla, el Excel se genera igual, sin logo.
     */
    private async cargarLogoConFallback(): Promise<ArrayBuffer | null> {
        try {
            const baseHref =
                document.querySelector('base')?.getAttribute('href') || '/';
            const logoPath = `${baseHref}assets/images/logos/logotipo-fondo-claro.png`;
            const logoResponse = await fetch(logoPath);
            if (logoResponse.ok) {
                return await logoResponse.arrayBuffer();
            }
            const altResponse = await fetch(
                'assets/images/logos/logotipo-fondo-claro.png'
            );
            if (altResponse.ok) {
                return await altResponse.arrayBuffer();
            }
            return null;
        } catch (error) {
            this.logger.warn('No se pudo cargar el logo', LOG_CONTEXT);
            return null;
        }
    }

    /**
     * Arma el workbook de UNA liquidación (hojas Totales/Agenciamiento/Pallets/
     * etc.) y lo descarga. Usado por el Excel individual.
     */
    private async crearYDescargarExcelLiquidacion(
        data: unknown,
        nombreArchivo: string
    ): Promise<SnackbarMensaje> {
        try {
            const workbook = new ExcelJS.Workbook();
            const colors = { ...OPB_COLORS };
            const logoArrayBuffer = await this.cargarLogoConFallback();

            await agregarHojasLiquidacionAlWorkbookUtil(
                workbook,
                data,
                null,
                colors,
                logoArrayBuffer,
                () => this.logger.warn('Error al agregar logo', LOG_CONTEXT)
            );

            if (workbook.worksheets.length === 0) {
                return {
                    texto: 'No hay datos para generar el archivo Excel.',
                    duracionMs: 3000,
                    panelClass: ['warning-snackbar'],
                };
            }

            const buffer = await workbook.xlsx.writeBuffer();
            this.descargarBuffer(buffer, nombreArchivo);

            return {
                texto: 'Excel generado exitosamente',
                duracionMs: 3000,
                panelClass: ['success-snackbar'],
            };
        } catch (error) {
            this.logger.error(
                'Error al crear el archivo Excel',
                error instanceof Error ? error : new Error(String(error)),
                LOG_CONTEXT
            );
            return {
                texto: 'Error al procesar los datos para el Excel',
                duracionMs: 5000,
                panelClass: ['error-snackbar'],
            };
        }
    }

    /**
     * Arma el workbook FUSIONADO de N liquidaciones (misma estructura de hojas
     * que el individual, con las filas concatenadas) y lo descarga.
     */
    private async crearYDescargarExcelFusionado(
        responses: Array<{ data: unknown }>
    ): Promise<SnackbarMensaje> {
        try {
            const workbook = new ExcelJS.Workbook();
            const colors = { ...OPB_COLORS };
            let logoArrayBuffer: ArrayBuffer | null = null;
            try {
                const baseHref =
                    document.querySelector('base')?.getAttribute('href') || '/';
                const logoPath = `${baseHref}assets/images/logos/logotipo-fondo-claro.png`;
                const logoResponse = await fetch(logoPath);
                if (logoResponse.ok) {
                    logoArrayBuffer = await logoResponse.arrayBuffer();
                }
            } catch {
                this.logger.warn('No se pudo cargar el logo', LOG_CONTEXT);
            }

            // Mismas hojas que el Excel individual: Totales, Agenciamiento, Pallets, Servicios Extra, Transportes.
            // En cada hoja se concatenan las filas de todas las liquidaciones seleccionadas.
            const dataList = responses.map(
                (r) =>
                    r.data as unknown as Record<
                        string,
                        { rows?: unknown[] } | undefined
                    >
            );
            // MEJ-04: en el fusionado, una columna de servicio se
            // muestra si CUALQUIER liquidación seleccionada la tiene.
            // Union de serviciosPresentes de todas las liquidaciones.
            const mergeServicios = (key: string): string[] => {
                const all = dataList.flatMap(
                    (d) =>
                        (
                            d?.[key] as {
                                serviciosPresentes?: string[];
                            }
                        )?.serviciosPresentes ?? []
                );
                return Array.from(new Set(all));
            };
            const combinedData: Record<
                string,
                { rows: unknown[]; serviciosPresentes?: string[] }
            > = {
                totales: {
                    rows: dataList.flatMap((d) => d?.['totales']?.rows ?? []),
                },
                contenedores: {
                    rows: dataList.flatMap(
                        (d) => d?.['contenedores']?.rows ?? []
                    ),
                    serviciosPresentes: mergeServicios('contenedores'),
                },
                pallets: {
                    rows: dataList.flatMap((d) => d?.['pallets']?.rows ?? []),
                },
                serviciosExtra: {
                    rows: dataList.flatMap(
                        (d) => d?.['serviciosExtra']?.rows ?? []
                    ),
                },
                transportes: {
                    rows: dataList.flatMap(
                        (d) => d?.['transportes']?.rows ?? []
                    ),
                    serviciosPresentes: mergeServicios('transportes'),
                },
                consolidado: {
                    // ERR-02a: se pasa AGRUPADO por liquidación (no
                    // aplanado) para que las filas sin contenedor de
                    // liquidaciones distintas nunca se fusionen entre sí.
                    rows: mergeConsolidadoByContainer(
                        dataList.map(
                            (d) =>
                                (d?.['consolidado']?.rows ??
                                    []) as ConsolidadoRow[]
                        )
                    ),
                },
            };
            await agregarHojasLiquidacionAlWorkbookUtil(
                workbook,
                combinedData,
                null,
                colors,
                logoArrayBuffer,
                () => this.logger.warn('Error al agregar logo', LOG_CONTEXT)
            );
            if (workbook.worksheets.length === 0) {
                return {
                    texto: 'No hay datos para generar el archivo Excel.',
                    duracionMs: 3000,
                    panelClass: ['warning-snackbar'],
                };
            }
            const buffer = await workbook.xlsx.writeBuffer();
            this.descargarBuffer(
                buffer,
                `liquidaciones_fusionado_${new Date()
                    .toISOString()
                    .slice(0, 10)}.xlsx`
            );
            return {
                texto: 'Excel fusionado generado exitosamente',
                duracionMs: 3000,
                panelClass: ['success-snackbar'],
            };
        } catch (error) {
            this.logger.error(
                'Error al crear Excel fusionado',
                error instanceof Error ? error : new Error(String(error)),
                LOG_CONTEXT
            );
            return {
                texto: 'Error al procesar el Excel fusionado',
                duracionMs: 5000,
                panelClass: ['error-snackbar'],
            };
        }
    }

    private descargarBuffer(buffer: any, nombreArchivo: string): void {
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = nombreArchivo;
        link.click();
        window.URL.revokeObjectURL(url);
    }

    /**
     * Excel individual (v1 o v2 según `ParameterService`). Equivalente a lo que
     * antes era `LiquidacionesAgenciaComponent.generarExcel`.
     */
    generarExcelIndividual(
        liquidacion: LiquidacionAgenciaLocal,
        avisar: AvisarSnackbar
    ): Observable<void> {
        this.logger.info(
            `Generando Excel para liquidación: ${liquidacion.numeroLiquidacion}`,
            LOG_CONTEXT
        );

        return this.parameterService.getLiquidacionAgenciaExcelVersion().pipe(
            switchMap((version) => {
                this.logger.info(
                    `📊 Usando versión Excel: ${version}`,
                    LOG_CONTEXT
                );
                return this.liquidacionesService.getLiquidacionAgenciaExcelDataNormalized(
                    liquidacion.id,
                    version
                );
            }),
            catchError((error) => {
                this.logger.error(
                    'Error al obtener datos para Excel desde el backend',
                    error as Error,
                    LOG_CONTEXT
                );
                return of({
                    data: null,
                    extraServiciosFailed: false,
                });
            }),
            switchMap((response) => {
                if (!response.data) {
                    this.logger.error(
                        'La respuesta del servicio para Excel no fue exitosa o no contenía datos.',
                        new Error('Respuesta de API inválida'),
                        LOG_CONTEXT
                    );
                    avisar({
                        texto: 'No se pudieron obtener los datos para el Excel',
                        duracionMs: 5000,
                        panelClass: ['error-snackbar'],
                    });
                    return of(undefined);
                }

                if (response.extraServiciosFailed) {
                    avisar({
                        texto: EXTRA_SERVICIOS_FALLBACK_MESSAGE,
                        duracionMs: 7000,
                        panelClass: ['warning-snackbar'],
                    });
                }

                return from(
                    this.crearYDescargarExcelLiquidacion(
                        response.data,
                        `liquidacion_${liquidacion.numeroLiquidacion}.xlsx`
                    )
                ).pipe(
                    map((mensaje) => {
                        avisar(mensaje);
                        this.logger.info(
                            `✅ Excel generado con estilos para liquidación: ${liquidacion.numeroLiquidacion}`,
                            LOG_CONTEXT
                        );
                        return undefined;
                    })
                );
            })
        );
    }

    /**
     * Excel fusionado de N liquidaciones seleccionadas. Equivalente a lo que
     * antes era `LiquidacionesAgenciaComponent.generarExcelFusionado`.
     *
     * Precondición: `ids` no está vacío — esa validación (y su snackbar
     * "Seleccione al menos una liquidación") se queda en el componente, porque
     * ahí NUNCA se activaba la flag `isGeneratingExcelFusionado` y es más simple
     * no llamar al servicio para ese caso que modelarlo acá.
     */
    generarExcelFusionado(
        ids: number[],
        avisar: AvisarSnackbar
    ): Observable<void> {
        // RESILIENCIA (OPB 2026-10-01 / PR #368 + 2026-10-03): antes, una
        // falla de Extra-Servicios acá (404/500/timeout) se propagaba y el
        // `catchError` de más abajo ABORTABA todo el Excel fusionado,
        // aunque el resto de los datos sí estaban disponibles. Ahora se
        // captura ACÁ, se loguea, se avisa con un snackbar, y el export
        // continúa con la hoja "Servicios Extra" mostrando un aviso
        // explícito (ver `getLiquidacionAgenciaExcelDataNormalized` /
        // `resolveServiciosExtraRows`) en vez de quedar vacía en silencio.
        return this.parameterService.getLiquidacionAgenciaExcelVersion().pipe(
            switchMap((version) =>
                (version === 'v2'
                    ? this.liquidacionesService
                          .getExtraServiciosTabForIds(ids)
                          .pipe(
                              map((extraServiciosPorId) => ({
                                  extraServiciosPorId,
                                  extraServiciosFailed: false,
                              })),
                              catchError((error) => {
                                  this.logger.error(
                                      'No se pudo obtener Extra-Servicios para el Excel fusionado',
                                      error as Error,
                                      LOG_CONTEXT
                                  );
                                  avisar({
                                      texto: EXTRA_SERVICIOS_FALLBACK_MESSAGE,
                                      duracionMs: 7000,
                                      panelClass: ['warning-snackbar'],
                                  });
                                  return of({
                                      extraServiciosPorId: new Map<
                                          number,
                                          ExtraRows
                                      >(),
                                      extraServiciosFailed: true,
                                  });
                              })
                          )
                    : of({
                          extraServiciosPorId: new Map<number, ExtraRows>(),
                          extraServiciosFailed: false,
                      })
                ).pipe(
                    switchMap(({ extraServiciosPorId, extraServiciosFailed }) =>
                        forkJoin(
                            ids.map((id) =>
                                this.liquidacionesService.getLiquidacionAgenciaExcelDataNormalized(
                                    id,
                                    version,
                                    version === 'v2'
                                        ? extraServiciosPorId.get(id) ?? []
                                        : undefined,
                                    extraServiciosFailed
                                )
                            )
                        )
                    )
                )
            ),
            catchError((error) => {
                this.logger.error(
                    'Error al obtener datos para Excel fusionado',
                    error as Error,
                    LOG_CONTEXT
                );
                avisar({
                    texto: 'Error al obtener los datos para el Excel fusionado',
                    duracionMs: 5000,
                    panelClass: ['error-snackbar'],
                });
                return of([]);
            }),
            switchMap((responses) => {
                if (
                    !Array.isArray(responses) ||
                    responses.length === 0 ||
                    responses.some((r) => !r?.data)
                ) {
                    avisar({
                        texto: 'No se pudieron obtener los datos para el Excel fusionado',
                        duracionMs: 5000,
                        panelClass: ['error-snackbar'],
                    });
                    return of(undefined);
                }
                return from(this.crearYDescargarExcelFusionado(responses)).pipe(
                    map((mensaje) => {
                        avisar(mensaje);
                        return undefined;
                    })
                );
            })
        );
    }

    /**
     * Excel del listado completo (todas las liquidaciones que cumplen el
     * filtro actual, o solo la selección si hay alguna). Equivalente a lo que
     * antes era `LiquidacionesAgenciaComponent.exportarExcel`.
     */
    async exportarExcelListado(
        filtros: EstadoFiltrosLiquidaciones,
        idsSeleccionados: number[],
        mapearLiquidaciones: (
            liquidaciones: LiquidacionAgenciaService[]
        ) => LiquidacionAgenciaLocal[],
        formatearMesAnio: (mesAnio: string) => string,
        avisar: AvisarSnackbar
    ): Promise<void> {
        this.logger.info(
            '📊 Exportando Excel de todas las liquidaciones (sin paginación)',
            LOG_CONTEXT
        );

        try {
            // MISMOS filtros que el listado en pantalla. Antes se armaban acá
            // aparte y faltaban `tipoId`, `nroLiquidacion`, `nroFactura`,
            // `nroNotaCobro` y `estadoId`: el Excel traía un conjunto DISTINTO
            // del que el usuario estaba viendo.
            const filters = construirFiltrosLiquidaciones(filtros, {
                page: 1,
                limit: TAMANO_PAGINA_EXPORT,
            });

            this.logger.info(
                `📋 [EXPORT EXCEL] Filtros aplicados: ${JSON.stringify(
                    filters
                )}`,
                LOG_CONTEXT
            );

            // Obtener todas las liquidaciones que cumplan los filtros
            // DESCARGA PAGINADA. Antes se pedía `limit: 10000` de una vez y esa
            // petición MUERE POR TIMEOUT: medido el 2026-09-04 contra test,
            // limit 500, 1000 y 10000 devuelven 502; con 200 responde. O sea que
            // exportar un conjunto grande ya fallaba, sólo que no se notaba
            // porque el filtro de temporada venía recortando el universo.
            const pedirPagina = (pagina: number) =>
                this.liquidacionesService
                    .getLiquidacionesAgencia({ ...filters, page: pagina })
                    .pipe(
                        catchError((error) => {
                            this.logger.error(
                                'Error al obtener liquidaciones para Excel',
                                error as Error,
                                LOG_CONTEXT
                            );
                            avisar({
                                texto: 'Error al obtener datos para exportar',
                                duracionMs: 5000,
                                panelClass: ['error-snackbar'],
                            });
                            throw error;
                        })
                    )
                    .toPromise();

            const primera = await pedirPagina(1);
            const acumulado = [...(primera?.items ?? [])];
            const totalEsperado = primera?.total ?? acumulado.length;

            let pagina = 1;
            while (
                acumulado.length < totalEsperado &&
                pagina < MAX_PAGINAS_EXPORT
            ) {
                pagina++;
                const siguiente = await pedirPagina(pagina);
                const lote = siguiente?.items ?? [];
                if (lote.length === 0) break;
                acumulado.push(...lote);
            }

            // 🔴 NUNCA truncar en silencio. Si el tope se alcanzó y todavía
            // faltan filas, el archivo estaría INCOMPLETO y se vería igual de
            // válido que uno completo.
            if (acumulado.length < totalEsperado) {
                avisar({
                    texto:
                        `La descarga quedó incompleta (${acumulado.length} de ${totalEsperado}). ` +
                        `Acota los filtros e intenta de nuevo.`,
                    duracionMs: 8000,
                    panelClass: ['error-snackbar'],
                });
                return;
            }

            const response = primera
                ? { ...primera, items: acumulado }
                : primera;

            if (!response || !response.items || response.items.length === 0) {
                avisar({
                    texto: 'No hay datos para exportar con los filtros aplicados',
                    duracionMs: 3000,
                    panelClass: ['warning-snackbar'],
                });
                return;
            }

            // Mapear datos a formato local
            let todasLasLiquidaciones = mapearLiquidaciones(
                response.items || []
            );

            // ERR-01: si hay liquidaciones seleccionadas con el check, el Excel
            // debe contener SOLO esas, no todo el listado filtrado.
            const exportandoSeleccion = idsSeleccionados.length > 0;
            if (exportandoSeleccion) {
                todasLasLiquidaciones = filtrarPorSeleccion(
                    todasLasLiquidaciones,
                    idsSeleccionados
                );

                // 🔴 EL GUARD QUE FALTABA. El chequeo de "no hay datos" corre
                // ANTES, sobre la respuesta de la API — que sí traía filas. Si la
                // intersección con lo seleccionado queda vacía, sin esto el Excel
                // se generaba igual: encabezados y una fila de totales en cero.
                //
                // Es exactamente lo que recibió OPB con la liquidación 1623 el
                // 2026-09-04. Un archivo que parece válido y no lo es es peor que
                // un error: se manda al cliente sin que nadie lo revise.
                if (todasLasLiquidaciones.length === 0) {
                    avisar({
                        texto: 'Las liquidaciones seleccionadas no están en el resultado de los filtros actuales. Revisá los filtros o quitá la selección.',
                        duracionMs: 8000,
                        panelClass: ['warning-snackbar'],
                    });
                    return;
                }
            }

            this.logger.info(
                `✅ [EXPORT EXCEL] Obtenidas ${todasLasLiquidaciones.length} liquidaciones para exportar`,
                LOG_CONTEXT
            );

            // La fila TOTALES SIEMPRE se calcula sumando las filas exportadas, nunca
            // desde `response.resumen`. El resumen es un cálculo aparte del backend
            // sobre otro universo de filas: si un día no coincide con las filas del
            // archivo (OPB 2026-10-21), la fila TOTALES dejaba de sumar lo que tiene
            // arriba y el Excel se veía válido igual. Calculando desde las filas, los
            // totales y las filas no pueden divergir.
            const totalesExportacion: LiquidacionAgenciaTotales = {
                cntr: 0,
                plts: 0,
                agenciamiento: 0,
                transporte: 0,
                gate: 0,
                osVenta: 0,
                totalVentaNeto: 0,
                totalNeto: 0,
                osTraspaso: 0,
                total: 0,
            };
            todasLasLiquidaciones.forEach((item) => {
                totalesExportacion.cntr += item.cntr;
                totalesExportacion.plts += item.plts;
                totalesExportacion.agenciamiento += item.agenciamiento;
                totalesExportacion.transporte += item.transporte;
                totalesExportacion.gate += item.gate;
                totalesExportacion.osVenta += item.osVenta;
                totalesExportacion.totalVentaNeto += item.totalVentaNeto;
                totalesExportacion.totalNeto += item.totalNeto;
                totalesExportacion.osTraspaso += item.osTraspaso;
                totalesExportacion.total += item.total;
            });

            // La porción Venta del gate, para que el total de OS Venta se calcule
            // con la MISMA fórmula que cada fila. Restar el gate completo acá daba
            // un total NEGATIVO con la columna toda en positivo: reportado por OPB
            // el 2026-09-03, el Excel mostraba -452.820 cuando la columna sumaba
            // 711.000 — la diferencia era el gate de traspaso de la liq 1696.
            totalesExportacion.gateVenta = todasLasLiquidaciones.reduce(
                (s, it) => s + (it.gateVenta || 0),
                0
            );
            // MEJ-02 (D8): total de "OS Traspaso" (sin gate), misma fórmula que
            // cada fila. `gate` completo para derivar gateTraspaso, igual que el
            // mapper del backend.
            const totalGate = todasLasLiquidaciones.reduce(
                (s, it) => s + (it.gate || 0),
                0
            );
            const totalGateTraspaso =
                totalGate - (totalesExportacion.gateVenta || 0);
            totalesExportacion.osTraspasoSinGate =
                (totalesExportacion.osTraspaso || 0) - totalGateTraspaso;

            // Crear workbook con exceljs
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Liquidaciones Agencia');

            // Colores de marca OPB
            const colors = { ...OPB_COLORS };

            // Cargar logo usando la ruta base de Angular
            let logoArrayBuffer: ArrayBuffer | null = null;
            try {
                // Obtener la ruta base del proyecto
                const baseHref =
                    document.querySelector('base')?.getAttribute('href') || '/';
                const logoPath = `${baseHref}assets/images/logos/logotipo-fondo-claro.png`;

                this.logger.debug(
                    `Intentando cargar logo desde: ${logoPath}`,
                    LOG_CONTEXT
                );

                const logoResponse = await fetch(logoPath);
                if (logoResponse.ok) {
                    logoArrayBuffer = await logoResponse.arrayBuffer();
                    this.logger.info(
                        `✅ Logo cargado exitosamente (${logoArrayBuffer.byteLength} bytes)`,
                        LOG_CONTEXT
                    );
                } else {
                    this.logger.warn(
                        `Logo no encontrado: ${logoResponse.status} ${logoResponse.statusText}`,
                        LOG_CONTEXT
                    );
                    // Intentar ruta alternativa sin base href
                    const altPath =
                        'assets/images/logos/logotipo-fondo-claro.png';
                    const altResponse = await fetch(altPath);
                    if (altResponse.ok) {
                        logoArrayBuffer = await altResponse.arrayBuffer();
                        this.logger.info(
                            `✅ Logo cargado desde ruta alternativa`,
                            LOG_CONTEXT
                        );
                    }
                }
            } catch (error) {
                this.logger.warn(`Error al cargar logo: ${error}`, LOG_CONTEXT);
            }

            // Definir columnas con headers en español (sin agregar aún)
            //
            // Punto B (OPB, ago-2026): "Nro. Factura" y "Nro. Nota de Cobro"
            // abren la hoja, antes de N° Liquidación, como muestra el documento.
            // Los datos llegan del listado (el backend los resuelve desde
            // tblShippingLiquidationCustomerInvoices, la misma fuente que los
            // filtros del punto A.1).
            const columnHeaders = [
                { header: 'Nro. Factura', key: 'nroFactura', width: 15 },
                {
                    header: 'Nro. Nota de Cobro',
                    key: 'nroNotaCobro',
                    width: 18,
                },
                {
                    header: 'N° Liquidación',
                    key: 'numeroLiquidacion',
                    width: 15,
                },
                { header: 'Mes/Año', key: 'mesAnio', width: 12 },
                { header: 'Cliente', key: 'cliente', width: 25 },
                { header: 'Estado', key: 'estado', width: 12 },
                { header: 'Tipo', key: 'tipo', width: 12 },
                { header: 'CNTR', key: 'cntr', width: 10 },
                { header: 'PLTS', key: 'plts', width: 10 },
                {
                    header: 'Agenciamiento',
                    key: 'agenciamiento',
                    width: 15,
                },
                { header: 'Transporte', key: 'transporte', width: 15 },
                { header: 'OS Venta', key: 'osVenta', width: 15 },
                {
                    header: 'Total Neto',
                    key: 'totalNeto',
                    width: 18,
                },
                // MEJ-02 (D8, OPB solicitud de mejoras 24-09-2026): se quitan
                // "Gate Out" y "Gate In" y se agrega "OS Traspaso" (osTraspaso
                // sin el gate). "Total Traspaso Bruto" sigue siendo osTraspaso,
                // sin cambios: OS Traspaso + GateTraspaso == Total Traspaso Bruto.
                {
                    header: 'OS Traspaso',
                    key: 'osTraspasoSinGate',
                    width: 15,
                },
                {
                    header: 'Total Traspaso Bruto',
                    key: 'osTraspaso',
                    width: 18,
                },
                { header: 'Total', key: 'total', width: 15 },
            ];

            // Configurar anchos de columna primero
            columnHeaders.forEach((col, index) => {
                worksheet.getColumn(index + 1).width = col.width;
            });

            // Agregar logo en el header (fila 1, columna A)
            if (logoArrayBuffer) {
                try {
                    const logoId = workbook.addImage({
                        buffer: logoArrayBuffer,
                        extension: 'png',
                    });
                    // Logo en columna A, fila 1, con tamaño adecuado
                    worksheet.addImage(logoId, {
                        tl: { col: 0.2, row: 0.2 },
                        ext: { width: 150, height: 50 },
                    });
                    this.logger.debug(
                        'Logo agregado exitosamente',
                        LOG_CONTEXT
                    );
                } catch (error) {
                    this.logger.warn(
                        `Error al agregar logo: ${error}`,
                        LOG_CONTEXT
                    );
                }
            }

            // Título del reporte (fila 1, columna D)
            const titleRow = worksheet.getRow(1);
            titleRow.height = 40;
            const titleCell = worksheet.getCell('D1');
            titleCell.value = 'LIQUIDACIONES AGENCIA';
            titleCell.font = {
                name: 'Calibri',
                size: 20,
                bold: true,
                color: { argb: 'FF' + colors.primary },
            };
            titleCell.alignment = {
                vertical: 'middle',
                horizontal: 'left',
            };
            worksheet.mergeCells('D1:H1');

            // Fecha de generación (fila 2, columna D)
            const dateRow = worksheet.getRow(2);
            dateRow.height = 25;
            const dateCell = worksheet.getCell('D2');
            dateCell.value = `Generado el: ${new Date().toLocaleDateString(
                'es-CL',
                {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                }
            )}`;
            dateCell.font = {
                name: 'Calibri',
                size: 11,
                color: { argb: 'FF' + colors.gray },
            };
            dateCell.alignment = { vertical: 'middle', horizontal: 'left' };
            worksheet.mergeCells('D2:H2');

            // Agregar una fila vacía
            worksheet.getRow(3).height = 10;

            // Definir las columnas manualmente para controlar dónde van los headers
            const headerRow = worksheet.getRow(4);
            headerRow.height = 30;

            columnHeaders.forEach((col, index) => {
                const cell = worksheet.getCell(4, index + 1);
                cell.value = col.header;

                // Aplicar estilos explícitamente a cada celda del header
                cell.font = {
                    name: 'Calibri',
                    size: 12,
                    bold: true,
                    color: { argb: 'FFFFFFFF' }, // Blanco
                };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF' + colors.primary },
                };
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: 'center',
                    wrapText: true,
                };
                cell.border = {
                    top: {
                        style: 'medium',
                        color: { argb: 'FF' + colors.dark },
                    },
                    bottom: {
                        style: 'medium',
                        color: { argb: 'FF' + colors.dark },
                    },
                    left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                    right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                };
            });

            // Agregar datos de todas las liquidaciones filtradas
            todasLasLiquidaciones.forEach((item, index) => {
                const rowNumber = 5 + index;
                const row = worksheet.getRow(rowNumber);

                // Los valores se enlazan por CLAVE, no por posición.
                //
                // ⚠️ Acá había 15 `row.getCell(N).value = ...` con los índices a mano,
                // y un comentario que describía el orden de 15 columnas. Cuando el
                // punto B agregó "Nro. Factura" y "Nro. Nota de Cobro" AL PRINCIPIO
                // del encabezado, nadie corrió los datos dos posiciones: el Excel
                // salió con la liquidación bajo "Nro. Factura" y el cliente bajo
                // "N° Liquidación" (reportado por OPB el 2026-08-26).
                //
                // Enlazando por la clave de `columnHeaders`, agregar o mover una
                // columna no puede volver a desincronizar los datos: si falta un
                // valor la celda queda vacía, que se ve y se corrige, en vez de
                // correr toda la fila en silencio.
                const valoresPorClave: Record<string, unknown> = {
                    nroFactura: item.nroFactura ?? '',
                    nroNotaCobro: item.nroNotaCobro ?? '',
                    numeroLiquidacion: item.numeroLiquidacion,
                    mesAnio: formatearMesAnio(item.mesAnio),
                    cliente: item.cliente,
                    estado: item.estado,
                    tipo: item.tipo,
                    cntr: item.cntr || 0,
                    plts: item.plts || 0,
                    agenciamiento: item.agenciamiento || 0,
                    transporte: item.transporte || 0,
                    // ⚠️ Se resta gateVenta, NO gate. `gate` incluye el gate de
                    // Traspaso, que vive en osTraspaso y nunca estuvo en osVenta:
                    // restarlo daba OS Venta NEGATIVA. Caso real reportado por OPB
                    // el 2026-09-01: liq 1696 (ENTRE RIOS) mostraba -$1.163.820.
                    // 67 liquidaciones en prod tienen gate de traspaso.
                    osVenta: (item.osVenta || 0) - (item.gateVenta || 0),
                    totalNeto: item.totalNeto || 0,
                    // MEJ-02 (D8): "OS Traspaso" reemplaza a Gate Out/In. Es
                    // osTraspaso SIN el gate; "Total Traspaso Bruto" sigue siendo
                    // osTraspaso completo, sin cambios.
                    osTraspasoSinGate:
                        item.osTraspasoSinGate ??
                        (item.osTraspaso || 0) -
                            ((item.gate || 0) - (item.gateVenta || 0)),
                    osTraspaso: item.osTraspaso || 0,
                    total: item.total || 0,
                };

                columnHeaders.forEach((columna, indice) => {
                    const valor = valoresPorClave[columna.key];
                    row.getCell(indice + 1).value =
                        (valor as string | number | null | undefined) ?? '';
                });

                /** Columnas que llevan número: alineación a la derecha y formato de miles. */
                const CLAVES_NUMERICAS = new Set([
                    'cntr',
                    'plts',
                    'agenciamiento',
                    'transporte',
                    'osVenta',
                    'totalNeto',
                    'osTraspasoSinGate',
                    'osTraspaso',
                    'total',
                ]);

                // Estilos de fila alternada - aplicar a cada celda individualmente
                const rowFillColor =
                    index % 2 === 0
                        ? { argb: 'FFFFFFFF' } // Blanco
                        : { argb: 'FF' + colors.light }; // Gris claro

                // Aplicar estilos a cada celda de la fila
                for (let col = 1; col <= columnHeaders.length; col++) {
                    const claveDeLaColumna = columnHeaders[col - 1].key;
                    const cell = row.getCell(col);

                    // Fondo alternado
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: rowFillColor,
                    };

                    // Fuente base
                    cell.font = {
                        name: 'Calibri',
                        size: 11,
                        color: { argb: 'FF000000' }, // Negro
                    };

                    // Alineación base
                    if (CLAVES_NUMERICAS.has(claveDeLaColumna)) {
                        // Números alineados a la derecha
                        cell.alignment = {
                            vertical: 'middle',
                            horizontal: 'right',
                        };
                    } else if (claveDeLaColumna === 'estado') {
                        // Estado centrado
                        cell.alignment = {
                            vertical: 'middle',
                            horizontal: 'center',
                        };
                    } else {
                        // Texto alineado a la izquierda
                        cell.alignment = {
                            vertical: 'middle',
                            horizontal: 'left',
                        };
                    }

                    // Bordes
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'FFd1d5db' } },
                        bottom: {
                            style: 'thin',
                            color: { argb: 'FFd1d5db' },
                        },
                        left: {
                            style: 'thin',
                            color: { argb: 'FFd1d5db' },
                        },
                        right: {
                            style: 'thin',
                            color: { argb: 'FFd1d5db' },
                        },
                    };
                }

                row.height = 22;

                // Formato de miles, también resuelto por clave.
                const numberCellIndices = columnHeaders
                    .map((columna, indice) =>
                        CLAVES_NUMERICAS.has(columna.key) ? indice + 1 : -1
                    )
                    .filter((indice) => indice > 0);
                numberCellIndices.forEach((colIndex) => {
                    const cell = row.getCell(colIndex);
                    cell.numFmt = '#,##0';
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: 'right',
                    };
                });

                // Aplicar bordes a todas las celdas de la fila
                for (let col = 1; col <= columnHeaders.length; col++) {
                    const claveDeLaColumna = columnHeaders[col - 1].key;
                    const cell = row.getCell(col);
                    if (!cell.border) {
                        cell.border = {
                            top: {
                                style: 'thin',
                                color: { argb: 'FFd1d5db' },
                            },
                            bottom: {
                                style: 'thin',
                                color: { argb: 'FFd1d5db' },
                            },
                            left: {
                                style: 'thin',
                                color: { argb: 'FFd1d5db' },
                            },
                            right: {
                                style: 'thin',
                                color: { argb: 'FFd1d5db' },
                            },
                        };
                    }
                }
            });

            // Fila de totales
            const totalRowIndex = 5 + todasLasLiquidaciones.length;
            const totalRow = worksheet.getRow(totalRowIndex);
            totalRow.height = 25;

            // Aplicar estilos a cada celda de totales individualmente
            // `columnHeaders.length`, no un 15 fijo: con el número a mano las dos
            // últimas columnas quedaban sin fondo ni borde.
            for (let col = 1; col <= columnHeaders.length; col++) {
                const cell = totalRow.getCell(col);

                // Fondo oscuro para todas las celdas
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF' + colors.dark },
                };

                // Fuente blanca y negrita
                cell.font = {
                    name: 'Calibri',
                    size: 12,
                    bold: true,
                    color: { argb: 'FFFFFFFF' }, // Blanco
                };

                // Alineación
                if (col === 1) {
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: 'left',
                    };
                } else if (col >= 6 && col <= 15) {
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: 'right',
                    };
                } else {
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: 'center',
                    };
                }
            }

            // Valores de totales, enlazados por CLAVE de `columnHeaders`.
            //
            // ⚠️ Acá había 10 `totalRow.getCell(N).value = ...` con los índices a
            // mano, y un comentario que describía un orden de 15 columnas que
            // arrancaba en "N° Liquidación". El encabezado real tiene 17 y arranca
            // con "Nro. Factura" y "Nro. Nota de Cobro": TODA la fila de totales
            // salía corrida DOS posiciones, con el total de CNTR bajo "Estado" y el
            // de OS Venta bajo "Gate Out". Las columnas "Total Traspaso Bruto" y
            // "Total" quedaban sin total. Reportado por OPB el 2026-09-03.
            //
            // Es el MISMO defecto que la lección #108, que se corrigió en las filas
            // de datos el 2026-08-26 — y esta fila, tres bloques más abajo, quedó
            // afuera. Enlazando por clave, agregar o mover una columna del
            // encabezado ya no puede desplazar los totales.
            const totalesPorClave: Record<string, number> = {
                cntr: totalesExportacion.cntr || 0,
                plts: totalesExportacion.plts || 0,
                agenciamiento: totalesExportacion.agenciamiento || 0,
                transporte: totalesExportacion.transporte || 0,
                // MISMA fórmula que cada fila: se resta la porción VENTA del gate,
                // no el gate completo. Restar el completo daba un total negativo
                // con la columna entera en positivo.
                osVenta:
                    (totalesExportacion.osVenta || 0) -
                    (totalesExportacion.gateVenta || 0),
                totalNeto: totalesExportacion.totalNeto || 0,
                // MEJ-02 (D8): reemplaza a Gate Out/In.
                osTraspasoSinGate: totalesExportacion.osTraspasoSinGate || 0,
                osTraspaso: totalesExportacion.osTraspaso || 0,
                total: totalesExportacion.total || 0,
            };

            columnHeaders.forEach((columna, indice) => {
                const cell = totalRow.getCell(indice + 1);
                if (indice === 0) {
                    cell.value = 'TOTALES';
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: 'left',
                    };
                } else if (columna.key in totalesPorClave) {
                    cell.value = totalesPorClave[columna.key];
                    cell.numFmt = '#,##0';
                } else {
                    cell.value = '';
                }
            });

            // Aplicar bordes a la fila de totales también
            // `columnHeaders.length`, no un 15 fijo: con el número a mano las dos
            // últimas columnas quedaban sin fondo ni borde.
            for (let col = 1; col <= columnHeaders.length; col++) {
                const cell = totalRow.getCell(col);
                cell.border = {
                    top: {
                        style: 'thin',
                        color: { argb: 'FF' + colors.dark },
                    },
                    bottom: {
                        style: 'thin',
                        color: { argb: 'FF' + colors.dark },
                    },
                    left: {
                        style: 'thin',
                        color: { argb: 'FF' + colors.dark },
                    },
                    right: {
                        style: 'thin',
                        color: { argb: 'FF' + colors.dark },
                    },
                };
            }

            // Generar archivo Excel
            const buffer = await workbook.xlsx.writeBuffer();
            this.descargarBuffer(
                buffer,
                `liquidaciones_agencia_${
                    new Date().toISOString().split('T')[0]
                }.xlsx`
            );

            this.logger.info('✅ Excel exportado exitosamente', LOG_CONTEXT);
            avisar({
                texto: 'Excel exportado exitosamente',
                duracionMs: 3000,
                panelClass: ['success-snackbar'],
            });
        } catch (error) {
            this.logger.error(
                '❌ Error al exportar Excel',
                error instanceof Error ? error : new Error(String(error)),
                LOG_CONTEXT
            );
            avisar({
                texto: 'Error al exportar Excel',
                duracionMs: 3000,
                panelClass: ['error-snackbar'],
            });
        }
    }
}
